import { useEffect, useState } from "react";
import useSmsTemplate from "../../../hooks/useSmsTemplate";
import { FiCopy } from "react-icons/fi";
import { KEY_OPTIONS, TYPE_OPTIONS } from "../../../data/sms/smsOptions";

const AddTemplateModal = ({
  open,
  onClose,
  setAffect,
  initialData,
  setEditItem,
}) => {
  const [formData, setFormData] = useState({
    key: "",
    content: "",
    type: "",
    description: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [templateVariables, setTemplateVariables] = useState({});
  const [variablesError, setVariablesError] = useState(null);
  const [showAllVars, setShowAllVars] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState("");

  const { createSmsTemplate, updateSmsTemplate, getSmsTemplateVariables } =
    useSmsTemplate();

  console.log("initialData", initialData);

  useEffect(() => {
    if (initialData) {
      setFormData({
        key: initialData.key || "",
        content: initialData.content || "",
        type: initialData.type || "",
        description: initialData.description || "",
      });

      // Determine group from key
      const group = KEY_OPTIONS.find((g) =>
        g.options.some((opt) => opt.value === initialData.key)
      )?.group;

      setSelectedGroup(group || "");
    }
  }, [initialData]);

  const VARIABLES_LIMIT = 20;

  const validateForm = () => {
    const newErrors = {};

    if (!selectedGroup) {
      newErrors.group = "Notification Category is required";
    }

    if (!formData.key.trim()) {
      newErrors.key = "Notification Key is required";
    }

    if (!formData.type) {
      newErrors.type = "Type is required";
    }

    if (!formData.content.trim()) {
      newErrors.content = "Content is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCopy = (key) => {
    navigator.clipboard.writeText(`{{${key}}}`);
  };

  const getVariableType = (type) => {
    if (type === "TEXT") return "sms";
    if (type === "WHATSAPP") return "email";
    return null;
  };

  const variableType = getVariableType(formData.type);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async () => {
    const isValid = validateForm();
    if (!isValid) return;

    setLoading(true);

    const action = initialData ? updateSmsTemplate : createSmsTemplate;
    const payload = initialData
      ? { id: initialData.id, ...formData }
      : formData;
    const res = await action(setLoading, payload, initialData?.key);

    if (res?.success) {
      setLoading(false);
      setFormData({
        key: "",
        content: "",
        type: "",
        description: "",
      });
      setErrors({});
      setSelectedGroup("");
      setEditItem(null);
      setAffect((p) => p + 1);
      onClose();
    }
  };

  const fetchTemplateVariables = async () => {
    setLoading(true);
    try {
      const response = await getSmsTemplateVariables(setLoading);
      setTemplateVariables(response);
      setVariablesError(null);
    } catch (error) {
      console.error("Failed to fetch template variables:", error);
      setVariablesError(
        "Failed to fetch template variables. Please refresh and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplateVariables();
  }, []);

  if (!open) return null;

  const handleGroupChange = (e) => {
    const value = e.target.value;
    setSelectedGroup(value);
    setFormData((prev) => ({ ...prev, key: "" }));
    setErrors((prev) => ({ ...prev, group: "" }));
  };

  const selectedGroupOptions =
    KEY_OPTIONS.find((g) => g.group === selectedGroup)?.options || [];

  const groupError = errors.group;
  const keyError = errors.key;
  const typeError = errors.type;
  const contentError = errors.content;

  const handleClose = () => {
    setFormData({
      key: "",
      content: "",
      type: "",
      description: "",
    });
    setErrors({});
    setSelectedGroup("");
    setEditItem(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white w-full max-w-lg rounded-xl shadow-xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-xl font-semibold">
            {initialData ? "Edit Template" : "Create Template"}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Notification Category */}
        <div className="max-w-md space-y-4">
          <div>
            <select
              value={selectedGroup}
              onChange={handleGroupChange}
              className={`border rounded-md w-full px-3 py-2 ${
                groupError ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Select Notification Category</option>
              {KEY_OPTIONS?.map((group) => (
                <option key={group.group} value={group.group}>
                  {group.group}
                </option>
              ))}
            </select>
            {groupError && (
              <p className="text-red-500 text-xs mt-1">{groupError}</p>
            )}
          </div>

          {selectedGroup && (
            <div>
              <select
                name="key"
                value={formData.key}
                onChange={handleChange}
                className={`border rounded-md w-full px-3 py-2 ${
                  keyError ? "border-red-500" : "border-gray-300"
                }`}
              >
                <option value="">Select Notification Key</option>
                {selectedGroupOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {keyError && (
                <p className="text-red-500 text-xs mt-1">{keyError}</p>
              )}
            </div>
          )}
        </div>

        {/* Type */}
        <div className="mb-4">
          <label className="block mb-1 text-sm font-medium">Type</label>
          <select
            name="type"
            value={formData.type}
            onChange={handleChange}
            className={`border rounded-md w-full px-3 py-2 ${
              typeError ? "border-red-500" : "border-gray-300"
            }`}
          >
            <option value="">Select Type</option>
            {TYPE_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          {typeError && (
            <p className="text-red-500 text-xs mt-1">{typeError}</p>
          )}
        </div>

        {/* Variables */}
        {templateVariables && (
          <div className="mb-4">
            {variablesError ? (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                <span>{variablesError}</span>
              </div>
            ) : (
              <>
                <p className="text-xs font-medium text-gray-600 mb-2">
                  {variableType === "sms"
                    ? "SMS Variables"
                    : "WhatsApp Variables"}{" "}
                  (click to copy)
                </p>

                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                  {templateVariables[variableType]
                    ?.slice(
                      0,
                      showAllVars
                        ? templateVariables[variableType].length
                        : VARIABLES_LIMIT
                    )
                    .map((item) => (
                      <div
                        key={item}
                        className="flex items-center justify-between gap-1 border rounded px-2 py-1 text-xs bg-gray-50 hover:bg-gray-100"
                      >
                        <span className="truncate">{item}</span>
                        <button
                          type="button"
                          onClick={() => handleCopy(item)}
                          className="text-gray-500 hover:text-primary1"
                          title={`Copy {{${item}}}`}
                        >
                          <FiCopy size={14} />
                        </button>
                      </div>
                    ))}
                </div>

                {templateVariables[variableType]?.length > VARIABLES_LIMIT && (
                  <button
                    type="button"
                    onClick={() => setShowAllVars((prev) => !prev)}
                    className="mt-2 text-xs text-primary1 hover:underline"
                  >
                    {showAllVars ? "Show less" : "Show more"}
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* Content */}
        <div className="mb-4">
          <label className="block mb-1 text-sm font-medium">Content</label>
          <textarea
            name="content"
            rows={3}
            placeholder="Enter message content..."
            value={formData.content}
            onChange={handleChange}
            className={`border rounded-md w-full px-3 py-2 ${
              contentError ? "border-red-500" : "border-gray-300"
            }`}
          ></textarea>
          {contentError && (
            <p className="text-red-500 text-xs mt-1">{contentError}</p>
          )}
        </div>

        {/* Description */}
        <div className="mb-6">
          <label className="block mb-1 text-sm font-medium">Description</label>
          <textarea
            name="description"
            rows={2}
            placeholder="Describe what triggers this template..."
            value={formData.description}
            onChange={handleChange}
            className="border rounded-md w-full px-3 py-2 border-gray-300"
          ></textarea>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 mt-4">
          <button
            onClick={handleClose}
            className="px-4 py-2 border rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 bg-primary1 text-white rounded-md hover:opacity-90 disabled:opacity-50"
          >
            {loading
              ? initialData
                ? "Updating"
                : "Creating"
              : initialData
              ? "Update Template"
              : "Create Template"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddTemplateModal;
